/**
 * @fileoverview Process Service for managing organizational processes
 * Processes can be Verbal, Documented, or Undocumented
 * Documented processes link to documents in the document library
 */

import { Timestamp } from 'firebase/firestore';
import { createDocument } from './documentsService';
import { SupportingDocs } from '@/js/external-js/variableModules/namespaces/SupportingDocs.namespace';
import { getAuthUID } from '@/lib/authState';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

/** Process types */
export type ProcessType = 'verbal' | 'documented' | 'undocumented';

/** Process status */
export type ProcessStatus = 'Draft' | 'Active' | 'Under Review' | 'Archived';

/** Repeat frequency options */
export type ProcessRepeatFrequency = 'never' | 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annually';

/** Repeat configuration for recurring processes */
export interface ProcessRepeatConfig {
  frequency: ProcessRepeatFrequency;
  interval: number; // e.g., every 2 weeks
  startDate?: Date | Timestamp;
  endDate?: Date | Timestamp | null;
}

/** Document library reference for processes synced from document library */
export interface DocumentLibraryReference {
  /** Document library document ID */
  documentId: string;
  /** Document library's component ID (e.g., DOC-123) */
  documentComponentId?: string;
  /** Document version */
  documentVersion?: string;
  /** When the document was synced to processes */
  syncedAt: Date | Timestamp;
  /** File name for attachment display */
  fileName?: string;
  /** File URL for download/preview */
  fileUrl?: string;
}

/** Process interface */
export interface Process {
  id: string;
  /** Consumer-facing ID (PRO-1, PRO-2, etc.) */
  componentId?: string;
  name: string;
  description: string;
  type: ProcessType;
  status: ProcessStatus;
  owner: string;
  /** Document library document ID (for documented processes) - kept for backwards compatibility */
  documentId?: string;
  /** Full document library reference with metadata */
  documentLibraryRef?: DocumentLibraryReference;
  /** Related control references */
  relatedControls?: string[];
  /** Business unit */
  businessUnit?: string;
  /** Category/department */
  category?: string;
  /** Repeat/recurrence configuration */
  repeat?: ProcessRepeatConfig;
  /** Review date (for non-recurring) */
  reviewDate?: Date | Timestamp;
  /** Last review date */
  lastReviewDate?: Date | Timestamp;
  /** Next review date (calculated from repeat config or manual) */
  nextReviewDate?: Date | Timestamp;
  /** Created date */
  createdAt: Date | Timestamp;
  /** Created by user ID */
  createdBy: string;
  /** Last updated date */
  updatedAt: Date | Timestamp;
  /** Last updated by user ID */
  updatedBy: string;
  /** Additional metadata */
  metadata?: Record<string, any>;
}

/** Input for creating a new process */
export interface CreateProcessInput {
  name: string;
  description: string;
  type: ProcessType;
  status?: ProcessStatus;
  owner?: string;
  relatedControls?: string[];
  businessUnit?: string;
  category?: string;
  /** Repeat/recurrence configuration */
  repeat?: ProcessRepeatConfig;
  /** Review date (for non-recurring processes) */
  reviewDate?: Date;
  /** File to upload for documented processes */
  file?: File;
  /** Existing document ID to link (for documented processes) */
  existingDocumentId?: string;
}

/**
 * Get company ID helper
 */
const getCompanyID = () => window.companyID || '';

/**
 * Ensure all processes have a componentId (PRO-1, PRO-2, etc.)
 * Note: This is now handled by the backend API during process creation
 * This function remains for backwards compatibility but may be deprecated
 */
export async function ensureProcessComponentIds(companyID: string): Promise<number> {
  // Backend handles componentId generation automatically
  console.log('[ProcessService] ensureProcessComponentIds is now handled by the backend');
  return 0;
}

/**
 * Create a new process
 * For documented processes, this will also create a document in the document library
 */
export async function createProcess(
  companyID: string,
  input: CreateProcessInput
): Promise<Process> {
  const userId = getAuthUID() || 'unknown';
  const now = new Date();
  
  let documentId: string | undefined;
  
  // For documented processes, create or link a document
  if (input.type === 'documented') {
    if (input.existingDocumentId) {
      // Link to existing document
      documentId = input.existingDocumentId;
    } else if (input.file) {
      // Create new document in document library
      const docTemplate = {
        fileName: input.file.name,
        displayName: input.name,
        documentType: 'Procedure',
        status: input.status || 'Draft',
        uploadedBy: userId,
        procRef: input.relatedControls || [],
        businessUnit: input.businessUnit || '',
        classification: 'Business Confidential',
      } as any;
      
      const newDoc = await createDocument(docTemplate, input.file, companyID);
      documentId = newDoc.docID?.toString();
      console.log('[ProcessService] Created document for process:', documentId);
    }
  }
  
  // Calculate next review date based on repeat config or review date
  let nextReviewDate: Date | undefined;
  let reviewDate: Date | undefined = input.reviewDate;
  
  if (input.repeat && input.repeat.frequency !== 'never') {
    // For recurring processes, use start date as next review
    nextReviewDate = input.repeat.startDate instanceof Date 
      ? input.repeat.startDate 
      : input.repeat.startDate 
        ? new Date((input.repeat.startDate as any).seconds * 1000)
        : now;
  } else if (input.reviewDate) {
    // For non-recurring, use the specified review date
    nextReviewDate = input.reviewDate;
    reviewDate = input.reviewDate;
  }
  
  // Create process via API (componentId is generated by backend)
  const processData = {
    name: input.name,
    description: input.description,
    type: input.type,
    status: input.status || 'Draft',
    owner: input.owner || userId,
    documentId,
    relatedControls: input.relatedControls || [],
    businessUnit: input.businessUnit || '',
    category: input.category || '',
    repeat: input.repeat,
    reviewDate,
    nextReviewDate,
  };
  
  const res = await deiterateAPI.POST('/v1/processes', {
    mode: 'cors',
    backend: 'thalassa',
    headers: {
      'X-Tenancy-Id': companyID,
      'Company-ID': companyID,
      'X-User-Id': userId,
    },
    body: JSON.stringify(processData)
  });
  
  console.log('[ProcessService] Created process:', res.process?.id, 'componentId:', res.process?.componentId);
  
  return res.process as Process;
}

/**
 * Get all processes for a company
 */
export async function getProcesses(companyID: string): Promise<Process[]> {
  const res = await deiterateAPI.GET('/v1/processes', {
    mode: 'cors',
    backend: 'thalassa',
    headers: {
      'X-Tenancy-Id': companyID,
      'Company-ID': companyID,
    }
  });
  
  return (res.processes || []) as Process[];
}

/**
 * Get a single process by ID
 */
export async function getProcess(companyID: string, processId: string): Promise<Process | null> {
  try {
    const res = await deiterateAPI.GET(`/v1/processes/${processId}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: {
        'X-Tenancy-Id': companyID,
        'Company-ID': companyID,
      }
    });
    
    return res.process as Process || null;
  } catch (error) {
    console.error('[ProcessService] Error getting process:', error);
    return null;
  }
}

/**
 * Update a process
 */
export async function updateProcess(
  companyID: string,
  processId: string,
  updates: Partial<Omit<Process, 'id' | 'createdAt' | 'createdBy'>>
): Promise<void> {
  const userId = getAuthUID() || 'unknown';
  
  // Filter out undefined values before sending
  const cleanUpdates: Record<string, any> = {};
  for (const [key, value] of Object.entries(updates)) {
    if (value !== undefined) {
      cleanUpdates[key] = value;
    }
  }
  
  console.log('[ProcessService] Updating process:', processId, 'with:', cleanUpdates);
  
  const res = await deiterateAPI.PUT(`/v1/processes/${processId}`, {
    mode: 'cors',
    backend: 'thalassa',
    headers: {
      'X-Tenancy-Id': companyID,
      'Company-ID': companyID,
      'X-User-Id': userId,
    },
    body: JSON.stringify(cleanUpdates)
  });
  
  console.log('[ProcessService] Updated process:', processId, 'response:', res);
}

/**
 * Delete a process
 * Note: This does NOT delete the linked document from the document library
 */
export async function deleteProcess(companyID: string, processId: string): Promise<void> {
  await deiterateAPI.DELETE(`/v1/processes/${processId}`, {
    mode: 'cors',
    backend: 'thalassa',
    headers: {
      'X-Tenancy-Id': companyID,
      'Company-ID': companyID,
    }
  });
  console.log('[ProcessService] Deleted process:', processId);
}

/**
 * Sync document library procedures to the processes collection
 * This creates process entries for any document library procedures that don't already have one
 * Returns the number of new processes created
 */
export async function syncDocumentLibraryProcedures(
  companyID: string,
  docProcedures: Array<{
    id: string;
    name?: string;
    policy_name?: string;
    description?: string;
    status?: string;
    policy_status?: string;
    owner?: string;
    policy_history?: { policy_owner?: string };
    category?: string;
    businessUnit?: string;
    nextReviewDate?: Date;
    relatedControls?: string[];
    created?: Date;
    last_reviewed?: Date;
    componentID?: string;
    policy_version?: string;
    policy_metadata?: { policyType?: string; documentCategory?: string };
    fileName?: string;
    fileUrl?: string;
  }>
): Promise<number> {
  const userId = getAuthUID() || 'system';
  
  // Get existing processes via API
  const existingProcesses = await getProcesses(companyID);
  
  // Create a set of document IDs that already have a process entry
  const existingDocIds = new Set(
    existingProcesses
      .filter(p => p.documentId)
      .map(p => p.documentId)
  );
  
  // Find document library procedures that don't have a process entry
  const newProcedures = docProcedures.filter(doc => !existingDocIds.has(doc.id));
  
  if (newProcedures.length === 0) {
    console.log('[ProcessService] No new document library procedures to sync');
    return 0;
  }
  
  console.log(`[ProcessService] Syncing ${newProcedures.length} document library procedures to processes collection`);
  
  const now = new Date();
  let createdCount = 0;
  
  // Create process entries via API (componentId is generated by backend)
  for (const doc of newProcedures) {
    const documentLibraryRef: DocumentLibraryReference = {
      documentId: doc.id,
      documentComponentId: doc.componentID || undefined,
      documentVersion: doc.policy_version || '1.0',
      syncedAt: now,
      fileName: doc.fileName || undefined,
      fileUrl: doc.fileUrl || undefined,
    };
    
    const processData = {
      name: doc.name || doc.policy_name || 'Untitled Process',
      description: doc.description || '',
      type: 'documented' as ProcessType,
      status: (doc.status || doc.policy_status || 'Draft') as ProcessStatus,
      owner: doc.owner || doc.policy_history?.policy_owner || '',
      documentId: doc.id,
      documentLibraryRef,
      relatedControls: doc.relatedControls || [],
      category: doc.category || '',
      businessUnit: doc.businessUnit || '',
      reviewDate: doc.nextReviewDate || null,
      nextReviewDate: doc.nextReviewDate || null,
    };
    
    try {
      await deiterateAPI.POST('/v1/processes', {
        mode: 'cors',
        backend: 'thalassa',
        headers: {
          'X-Tenancy-Id': companyID,
          'Company-ID': companyID,
          'X-User-Id': userId,
        },
        body: JSON.stringify(processData)
      });
      createdCount++;
    } catch (error) {
      console.error(`[ProcessService] Error creating process for doc ${doc.id}:`, error);
    }
  }
  
  console.log(`[ProcessService] Successfully synced ${createdCount} procedures to processes collection`);
  return createdCount;
}

/**
 * Get a process by its linked document ID
 */
export async function getProcessByDocumentId(companyID: string, documentId: string): Promise<Process | null> {
  try {
    const res = await deiterateAPI.GET(`/v1/processes/by-document/${documentId}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: {
        'X-Tenancy-Id': companyID,
        'Company-ID': companyID,
      }
    });
    
    return res.process as Process || null;
  } catch (error) {
    console.error('[ProcessService] Error getting process by document ID:', error);
    return null;
  }
}

/**
 * Get process type display label
 */
export function getProcessTypeLabel(type: ProcessType): string {
  switch (type) {
    case 'verbal':
      return 'Verbal';
    case 'documented':
      return 'Documented';
    case 'undocumented':
      return 'Undocumented';
    default:
      return 'Unknown';
  }
}

/**
 * Get process type description
 */
export function getProcessTypeDescription(type: ProcessType): string {
  switch (type) {
    case 'verbal':
      return 'A process communicated verbally without formal documentation';
    case 'documented':
      return 'A formally documented process with supporting documentation';
    case 'undocumented':
      return 'A process that exists but has not yet been documented';
    default:
      return '';
  }
}

export default {
  createProcess,
  getProcesses,
  getProcess,
  updateProcess,
  deleteProcess,
  syncDocumentLibraryProcedures,
  getProcessByDocumentId,
  ensureProcessComponentIds,
  getProcessTypeLabel,
  getProcessTypeDescription,
};
